import React, { useState, useEffect, useCallback, useMemo } from 'react';
import uniqBy from 'lodash.uniqby';
import PropTypes from 'prop-types';
import Fuse from 'fuse.js';
import getCategories from '@core/spaceSelector/utils/getCategories';
import filterByCategory from '@core/spaceSelector/utils/filterByCategory';
import getSubCategories from '@core/spaceSelector/utils/getSubCategories';
import {
  Box,
  Separator,
  useTheme,
  Accordion,
  Area,
  Text,
} from '@ublend-npm/aulaui-core';
import useCategoryPreferences from '../hooks/useCategoryPreferences';
import SpaceList from './SpaceList';
import SpaceListAccordion from './SpaceListAccordion';
import StarSpaceOnboarding from './StarSpaceOnboarding';
import { StaffSpaces } from './StaffSpaces';
import {
  getAcademicYearStart,
  getAccordionPriority,
  getDisplayAccordionLabel,
} from './utils/accordionOrdering';

const Divider = () => <Separator sx={{ my: '0.5rem' }} />;
const starredCategory = {
  label: 'Starred spaces',
  order: -1,
};

const isValidSpace = (space) =>
  space &&
  typeof space === 'object' &&
  'archived' in space &&
  Array.isArray(space.categories);

const SpaceSelector = ({
  spaces,
  onSpaceSelect,
  onSpaceStar,
  onSubCategorySelect,
  search = '',
  showArchived = false,
  sort = 'az',
  fetchRecentlyViewedSpaces,
}) => {
  const [fuse, setFuse] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [recentlyUnstarredSpaces, setRecentlyUnstarredSpaces] = useState([]);
  const [hasAppliedDefaultOpenState, setHasAppliedDefaultOpenState] =
    useState(false);

  const filteredSpaces = useMemo(() => {
    let validSpaces = spaces.filter(isValidSpace);

    if (!showArchived) {
      validSpaces = validSpaces.filter((s) => !s.archived);
    }

    if (!search || !fuse) {
      return validSpaces;
    }

    const fuseResults = fuse
      .search(search)
      .map((r) => r.item)
      .filter(isValidSpace);

    const directMatches = validSpaces.filter((space) =>
      space.name?.toLowerCase().includes(search.toLowerCase())
    );

    return uniqBy([...directMatches, ...fuseResults], 'id');
  }, [fuse, search, showArchived, spaces]);

  const starredSpacesForDisplay = useMemo(() => {
    return filteredSpaces
      .filter((s) => {
        const isCurrentlyStarred = s.isStarred;
        const isRecentlyUnstarred = recentlyUnstarredSpaces.some(
          (rs) => rs.id === s.id
        );
        return isCurrentlyStarred && !isRecentlyUnstarred;
      })
      .map((s) => ({ ...s, categories: [starredCategory] }))
      .sort((a, b) => {
        if (sort === 'az') {
          return a.name.localeCompare(b.name);
        } else if (sort === 'za') {
          return b.name.localeCompare(a.name);
        }
        return 0;
      });
  }, [filteredSpaces, recentlyUnstarredSpaces, sort]);

  const allSpacesForDisplay = useMemo(() => {
    return [...filteredSpaces].sort((a, b) => {
      if (sort === 'az') {
        return a.name.localeCompare(b.name);
      } else if (sort === 'za') {
        return b.name.localeCompare(a.name);
      }
      return 0;
    });
  }, [filteredSpaces, sort]);

  const [openedAccordions, setOpenedAccordions] = useState(new Set());
  const { palette } = useTheme();
  const categories = useMemo(
    () => [starredCategory, ...getCategories(spaces)],
    [spaces]
  );
  const accordionItems = useMemo(
    () =>
      categories.slice(1).flatMap((category) => {
        const subCategoriesOfCategory = getSubCategories(
          spaces,
          category.label
        );

        if (subCategoriesOfCategory.length > 0) {
          return subCategoriesOfCategory.map((subCategory) => ({
            type: 'subCategory',
            key: `${category.label}-${subCategory.label}`,
            categoryLabel: category.label,
            label: subCategory.label,
            order: subCategory.order ?? 0,
            yearStart: getAcademicYearStart(subCategory.label),
            spaces: filterByCategory(
              allSpacesForDisplay,
              category,
              subCategory
            ),
            category,
            subCategory,
          }));
        }

        return [
          {
            type: 'category',
            key: category.label,
            categoryLabel: category.label,
            label: category.label,
            order: category.order ?? 0,
            yearStart: getAcademicYearStart(category.label),
            spaces: filterByCategory(allSpacesForDisplay, category),
            category,
          },
        ];
      }),
    [allSpacesForDisplay, categories, spaces]
  );
  const sortedAccordionItems = useMemo(
    () =>
      [...accordionItems].sort((a, b) => {
        const priorityDelta = getAccordionPriority(a) - getAccordionPriority(b);

        if (priorityDelta !== 0) {
          return priorityDelta;
        }

        if (a.yearStart !== null && b.yearStart !== null) {
          return b.yearStart - a.yearStart;
        }

        return a.order - b.order;
      }),
    [accordionItems]
  );
  const { categoryPreferences, setSubCategoriesPreference } =
    useCategoryPreferences();

  let primaryCategory = null;

  if (categories.length > 0) {
    [primaryCategory] = categories;
  }

  useEffect(() => {
    if (categoryPreferences && categoryPreferences.category) {
      const category = categories.find(
        (c) => c.label === categoryPreferences.category.label
      );
      if (category) {
        setSelectedCategory(category);
      }
    } else {
      setSelectedCategory(primaryCategory);
    }
  }, [primaryCategory, categories, categoryPreferences]);

  useEffect(() => {
    if (selectedCategory && selectedCategory.label !== starredCategory.label) {
      setRecentlyUnstarredSpaces([]);
    }
  }, [selectedCategory]);

  useEffect(() => {
    setRecentlyUnstarredSpaces([]);
  }, [showArchived, sort, search]);

  useEffect(() => {
    setFuse(
      new Fuse(spaces, {
        threshold: 0.3,
        location: 0,
        distance: 1000,
        keys: ['name'],
        shouldSort: true,
      })
    );
  }, [spaces]);

  useEffect(() => {
    if (hasAppliedDefaultOpenState || categories.length <= 1) {
      return;
    }

    setOpenedAccordions(
      sortedAccordionItems.length > 0
        ? new Set([sortedAccordionItems[0].key])
        : new Set()
    );
    setHasAppliedDefaultOpenState(true);
  }, [categories.length, hasAppliedDefaultOpenState, sortedAccordionItems]);

  useEffect(() => {
    const tabbar = window.document.getElementById('space-settings-tabs');
    if (tabbar) {
      tabbar.style.display = 'none';
    }
    return () => {
      if (tabbar) {
        tabbar.style.display = 'block';
      }
    };
  }, []);

  const handleSpaceStar = async (spaceId, subCategory = null) => {
    const spaceFromProp = spaces.find((s) => s.id === spaceId);

    if (!spaceFromProp) return;

    if (spaceFromProp.isStarred) {
      setRecentlyUnstarredSpaces((rus) => {
        if (rus.some((s) => s.id === spaceId)) {
          return rus;
        }
        return [...rus, { ...spaceFromProp, isStarred: false }];
      });
    } else {
      setRecentlyUnstarredSpaces((rus) => rus.filter((s) => s.id !== spaceId));
    }

    await onSpaceStar({
      spaceId,
      showArchived,
      sort,
      search,
      category: selectedCategory?.label || null,
      subCategory,
    });

    fetchRecentlyViewedSpaces();
  };

  const handleSpaceSelect = (id, subCategory = null) => {
    onSpaceSelect(
      id,
      showArchived,
      sort,
      selectedCategory ? selectedCategory.label : null,
      subCategory
    );
  };

  const handleOpenedAccordion = (uniqueCatagory) => {
    if (openedAccordions.has(uniqueCatagory)) {
      openedAccordions.delete(uniqueCatagory);
    } else {
      openedAccordions.add(uniqueCatagory);
    }
    setOpenedAccordions(new Set([...openedAccordions]));
  };

  const RenderStarredSpaces = () => {
    if (!selectedCategory) {
      return null;
    }
    const spacesInStarredCategory = starredSpacesForDisplay;

    const allStaredArchived = spacesInStarredCategory?.every(
      (item) => item.archived === true
    );

    if (
      spacesInStarredCategory.length === 0 ||
      (allStaredArchived && !showArchived)
    ) {
      if (selectedCategory.label === starredCategory.label) {
        return <StarSpaceOnboarding />;
      }

      return (
        <Area>
          <Text>Your search didn’t match any of your spaces.</Text>
          <Text>Try with different keywords.</Text>
        </Area>
      );
    }

    return (
      <>
        <Accordion
          label={`Starred spaces (${spacesInStarredCategory.length})`}
          expanded={openedAccordions.has(starredCategory.label)}
          onChange={() => handleOpenedAccordion(starredCategory.label)}
          leftAligner
          detailsScrollAfter="24.5rem"
          showScrollbar={true}
          size="large"
          sx={{
            bgcolor: 'transparent',
            '& .start-summary .MuiTypography-subtitle2': {
              fontSize: '20px',
              fontWeight: 600,
            },
          }}
        >
          <SpaceList
            spaces={spacesInStarredCategory}
            onClick={handleSpaceSelect}
            onStar={handleSpaceStar}
          />
        </Accordion>
        <Divider />
      </>
    );
  };

  const RenderPrimarySpaces = () => {
    return sortedAccordionItems.map((item) => {
      if (item.type === 'subCategory') {
        return (
          <React.Fragment key={item.key}>
            <SpaceListAccordion
              spaces={item.spaces}
              label={item.label}
              onClick={(id) => {
                handleSpaceSelect(id, item.subCategory.label);
              }}
              onStar={(id) => handleSpaceStar(id, item.subCategory.label)}
              expanded={openedAccordions.has(item.key)}
              onChange={() => {
                handleOpenedAccordion(item.key);
                if (openedAccordions.has(item.key)) {
                  onSubCategorySelect(
                    item.category.label,
                    item.subCategory.label
                  );
                }
                setSubCategoriesPreference(item.subCategory);
              }}
            />
            <Divider />
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={item.key}>
          <Accordion
            label={`${getDisplayAccordionLabel(item.label)} (${
              item.spaces.length
            })`}
            size="large"
            leftAligner
            detailsScrollAfter="24.5rem"
            showScrollbar={true}
            expanded={openedAccordions.has(item.key)}
            onChange={() => handleOpenedAccordion(item.key)}
            sx={{
              bgcolor: 'transparent',
              '& .start-summary .MuiTypography-subtitle2': {
                fontSize: '20px',
                fontWeight: 600,
              },
            }}
          >
            <SpaceList
              spaces={item.spaces}
              onClick={handleSpaceSelect}
              onStar={handleSpaceStar}
            />
          </Accordion>
          <Divider />
        </React.Fragment>
      );
    });
  };

  return (
    <Box
      bgcolor={palette.background.default}
      display="flex"
      flexDirection="column"
      width="100%"
      height="calc(100% - 67px)"
      withHeaderDivider
      marginBottom={8}
    >
      <RenderStarredSpaces />
      <RenderPrimarySpaces />
      <StaffSpaces showArchived={showArchived} />
    </Box>
  );
};

SpaceSelector.propTypes = {
  spaces: PropTypes.arrayOf(PropTypes.shape({})),
  onSpaceSelect: PropTypes.func,
  onSpaceStar: PropTypes.func,
  onSubCategorySelect: PropTypes.func,
  search: PropTypes.string,
  showArchived: PropTypes.bool,
  sort: PropTypes.oneOf(['az', 'za']),
  fetchRecentlyViewedSpaces: PropTypes.func,
};

SpaceSelector.defaultProps = {
  spaces: [],
  onSpaceSelect: () => null,
  onSpaceStar: () => null,
  onSubCategorySelect: () => null,
  search: '',
  showArchived: false,
  sort: 'az',
  fetchRecentlyViewedSpaces: () => null,
};

export default React.memo(SpaceSelector);
